import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Center,
  Container,
  Grid,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { AlertTriangle } from 'react-feather';
import { useNavigate, useParams } from 'react-router-dom';
import {
  participantPostSessionApi,
  type FeedbackSidebarStatus,
  type ParticipantFeedbackPayload,
  type ParticipantPostSessionSummary,
} from '../../../services/api/participantPostSession';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { useAiInsightPolling } from '../../../hooks/useAiInsightPolling';
import { FeaturesDisabledPage } from '../../FeaturesDisabledPage';
// MED-1088 — reuse the peer-leader page's prep callout (AC2); do not fork it,
// so it inherits whatever polish that component receives (MED-1060).
import { NextSessionCallout } from '../peerLeaderPostSession/NextSessionCallout';
import { FinalizeCommitmentCard } from './FinalizeCommitmentCard';
import { LeaderVisibilitySidebarCard } from './LeaderVisibilitySidebarCard';
import { LeaveFeedbackCard } from './LeaveFeedbackCard';
import { NextSessionSidebarCard } from './NextSessionSidebarCard';
import { FullContentHeader } from '../../../components/layout/FullContentHeader';
import { SessionCompleteBadge } from '../../../components/session/SessionCompleteBadge';
import { PostSessionStepper, type PostSessionStep } from './PostSessionStepper';
import { ReviewNotesCard } from './ReviewNotesCard';
import { SessionSummarySidebarCard } from './SessionSummarySidebarCard';
import SessionThemesCard from '../../coach/components/post-session/SessionThemesCard';
import { composeSessionLabel } from '../sessionLabel';

/**
 * Participant Post-Session page (MED-806 / E3.4).
 *
 * Composition root for the structured post-session flow:
 *   Header → Stepper → (Leave Feedback, Review Notes, Finalize Commitment*,
 *   Done) accordion + right sidebar (Session Summary, Next Session, Leader
 *   Visibility).
 *
 * Step ordering (AC2 + AC6): Feedback is prioritized first; Review Notes
 * second; Finalize Commitment third (conditional on
 * `commitment_tracking_enabled`); Done last. All steps expanded on load;
 * completing a step collapses it (AC2).
 *
 * Gated by the `live_session_delivery` feature flag at both the API layer
 * (404 when off) and the client layer (FeaturesDisabledPage fallback).
 */
export interface ParticipantPostSessionPageProps {
  /**
   * MED-1040 — DEV-only static preview: seeds the summary and bypasses the
   * feature-flag gate + fetch so the full page renders from a fixture
   * (`/dev/post-session/participant`). Compiled out of production builds.
   */
  preview?: { summary: ParticipantPostSessionSummary };
}

export function ParticipantPostSessionPage({
  preview,
}: ParticipantPostSessionPageProps = {}) {
  const isPreview = !!preview;
  const params = useParams<{ sessionId: string }>();
  const sessionId = params.sessionId ?? preview?.summary.session_id;
  const navigate = useNavigate();
  const isFlagOn = useFeatureFlag('live_session_delivery');

  const [summary, setSummary] = useState<ParticipantPostSessionSummary | null>(
    preview?.summary ?? null
  );
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState<string | null>(null);

  // Step accordion expansion state. Each starts open per AC6.
  const [feedbackExpanded, setFeedbackExpanded] = useState(true);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [commitmentExpanded, setCommitmentExpanded] = useState(true);

  // Live overrides — these mutate as the participant interacts without
  // refetching summary. Server values seed them and successful writes update
  // them, so sidebar status pills update in real time (IV2).
  const [liveFeedback, setLiveFeedback] =
    useState<ParticipantFeedbackPayload | null>(
      preview?.summary.feedback ?? null
    );
  const [liveNotesReviewedAt, setLiveNotesReviewedAt] = useState<string | null>(
    preview?.summary.post_session_state.notes_reviewed_at ?? null
  );
  // MED-1058 AC9 — the commitment step completes only when the participant
  // explicitly Saves here. A pre-filled in-session commitment is NOT treated as
  // complete on load, so the step stays expanded until that discrete Save.
  // (There's no server "finalized" flag for commitments, unlike notes'
  // notes_reviewed_at, so this is tracked client-side for the visit.)
  const [commitmentFinalized, setCommitmentFinalized] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await participantPostSessionApi.getSummary(sessionId);
      setSummary(data);
      setLiveFeedback(data.feedback);
      setLiveNotesReviewedAt(data.post_session_state.notes_reviewed_at);
      // Collapse already-complete steps on load so the next incomplete step
      // is the top-of-view (AC6 explicitly calls this out for Step 1, and the
      // same UX makes sense for revisits to Step 2).
      if (data.feedback.status === 'submitted') setFeedbackExpanded(false);
      if (data.post_session_state.notes_reviewed_at) setNotesExpanded(false);
      // MED-1058 AC9 — do NOT collapse the commitment step for a pre-filled
      // in-session commitment; it stays expanded until the participant Saves
      // here (the discrete completion trigger).
    } catch (err) {
      const e = err as Error & { status?: number };
      if (e?.status === 403) {
        setError('You are not enrolled in this session.');
      } else if (e?.status === 404) {
        setError('Session not found or unavailable.');
      } else {
        setError(e?.message ?? 'Failed to load post-session page.');
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isPreview) return;
    void fetchSummary();
  }, [fetchSummary, isPreview]);

  // MED-1239 — auto-resolve the async Session Themes insight without a hard
  // refresh. Refetch the summary and merge ONLY `themes` into state; the live
  // override state (liveFeedback / liveNotesReviewedAt / commitmentFinalized)
  // is left untouched, and the spread keeps the same `feedback` reference so
  // the LeaveFeedbackCard's `initial` prop never changes — in-progress feedback
  // edits are not clobbered by a poll tick (AC5). Poll failures are swallowed.
  const pollThemes = useCallback(async () => {
    if (!sessionId) return;
    try {
      const fresh = await participantPostSessionApi.getSummary(sessionId);
      setSummary(prev => (prev ? { ...prev, themes: fresh.themes } : prev));
    } catch {
      // Intentionally ignored — a transient poll failure must not toast/blank.
    }
  }, [sessionId]);

  // Themes is the only pending-capable AI envelope on the participant view
  // (no airtime card here). Stops on terminal state (AC3), bounded (AC4),
  // disabled in DEV preview.
  const themesPending = summary?.themes?.status === 'pending';
  useAiInsightPolling({
    enabled: !isPreview && !!sessionId,
    pending: !!themesPending,
    poll: pollThemes,
  });

  // ----- derived completion booleans + done state ------------------------
  const feedbackStatus =
    liveFeedback?.status ?? summary?.feedback.status ?? 'draft';
  const feedbackComplete = feedbackStatus === 'submitted';
  const notesComplete = !!liveNotesReviewedAt;
  // AC9 — completion is the discrete Save in this step, not pre-filled text.
  const commitmentComplete = commitmentFinalized;
  const showCommitmentStep = summary?.commitment_tracking_enabled === true;
  const isDone =
    feedbackComplete &&
    notesComplete &&
    (showCommitmentStep ? commitmentComplete : true);

  // Sidebar feedback status reflects live edits, not the original snapshot.
  const liveSidebarStatus: FeedbackSidebarStatus | undefined = useMemo(() => {
    if (!liveFeedback) return undefined;
    if (liveFeedback.status === 'submitted') return 'submitted';
    const hasAnyContent =
      liveFeedback.satisfaction_rating != null ||
      liveFeedback.present_intentional != null ||
      liveFeedback.group_understood != null ||
      liveFeedback.felt_open != null ||
      liveFeedback.thoughts_expanded != null ||
      liveFeedback.action_step != null ||
      liveFeedback.what_enjoyed.trim().length > 0 ||
      liveFeedback.what_improvements.trim().length > 0 ||
      liveFeedback.anything_else.trim().length > 0 ||
      liveFeedback.what_for_next.trim().length > 0;
    return hasAnyContent ? 'in_progress' : 'not_started';
  }, [liveFeedback]);

  // ----- stepper activeStep derivation ----------------------------------
  // First incomplete step wins. Mantine indexes from 0; the "Done" bubble
  // sits at the last index. AC14: when fully done, stepper highlights Done.
  const activeStep = useMemo(() => {
    const steps: { complete: boolean }[] = [
      { complete: feedbackComplete },
      { complete: notesComplete },
    ];
    if (showCommitmentStep) steps.push({ complete: commitmentComplete });
    const firstIncomplete = steps.findIndex(s => !s.complete);
    return firstIncomplete === -1 ? steps.length : firstIncomplete;
  }, [feedbackComplete, notesComplete, commitmentComplete, showCommitmentStep]);

  const handleStepClick = useCallback((step: PostSessionStep) => {
    if (step === 'feedback') setFeedbackExpanded(true);
    if (step === 'notes') setNotesExpanded(true);
    if (step === 'commitment') setCommitmentExpanded(true);
  }, []);

  // ----- early returns ---------------------------------------------------
  // MED-1180 — defense-in-depth: also disable when the per-cohort LSD flag is
  // false on the returned summary payload (backend already 404s a non-LSD cohort).
  if (!isPreview && (!isFlagOn || summary?.live_session_delivery_enabled === false)) {
    return <FeaturesDisabledPage />;
  }
  if (loading) {
    return (
      <Center mih={400}>
        <Loader />
      </Center>
    );
  }
  if (error || !summary || !sessionId) {
    return (
      <Container size="md" py="lg">
        <Alert
          icon={<AlertTriangle size={18} />}
          color="red"
          title="Post-session page unavailable"
        >
          <Text size="sm">{error ?? 'Unknown error.'}</Text>
        </Alert>
      </Container>
    );
  }

  // ----- main render -----------------------------------------------------
  // MED-1146 (AC-C1/AC-L1/AC-L2) — the one participant label rule: display-aware
  // topic (else group name) composed as "Session N: …", never the raw
  // `{group} - Session N` title. No LSD branch — topic presence drives it.
  const sessionLabel = composeSessionLabel({
    session_number: summary.sidebar_session_summary.session_number,
    session_topic: summary.session_topic,
    group_name: summary.group_name,
    title: summary.session_title,
  });
  return (
    <>
      <FullContentHeader
        data-testid="post-session-header"
        title={sessionLabel}
        programName={summary.program_name}
        clientName={summary.organization_name}
        durationMinutes={summary.sidebar_session_summary.duration_minutes}
        onBack={() => navigate('/participant')}
        sticky
        accessory={<SessionCompleteBadge />}
      />
      <Container size="xl" py="lg">
        <Stack gap="lg">
          <Box data-testid="post-session-stepper">
            <PostSessionStepper
              activeStep={activeStep}
              feedbackComplete={feedbackComplete}
              notesComplete={notesComplete}
              commitmentComplete={commitmentComplete}
              showCommitmentStep={showCommitmentStep}
              isDone={isDone}
              onStepClick={handleStepClick}
            />
          </Box>

          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="lg">
                <LeaveFeedbackCard
                  sessionId={sessionId}
                  initial={summary.feedback}
                  available={summary.feedback_available}
                  expanded={feedbackExpanded}
                  onToggleExpand={() => setFeedbackExpanded(prev => !prev)}
                  onChange={setLiveFeedback}
                  onSubmitted={payload => {
                    setLiveFeedback(payload);
                    setFeedbackExpanded(false);
                  }}
                />

                <ReviewNotesCard
                  sessionId={sessionId}
                  initialNotesHtml={summary.notes_html}
                  reviewed={notesComplete}
                  expanded={notesExpanded}
                  onToggleExpand={() => setNotesExpanded(prev => !prev)}
                  onMarkReviewed={() => {
                    setLiveNotesReviewedAt(new Date().toISOString());
                    setNotesExpanded(false);
                  }}
                />

                {showCommitmentStep && (
                  <FinalizeCommitmentCard
                    sessionId={sessionId}
                    initialCommitmentText={summary.commitment_text}
                    authoredByCoach={summary.commitment_authored_by_coach}
                    saved={commitmentComplete}
                    expanded={commitmentExpanded}
                    onToggleExpand={() => setCommitmentExpanded(prev => !prev)}
                    onSaved={() => {
                      setCommitmentFinalized(true);
                      setCommitmentExpanded(false);
                    }}
                  />
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="lg">
                <SessionSummarySidebarCard
                  summary={summary.sidebar_session_summary}
                  liveFeedbackStatus={liveSidebarStatus}
                />
                {/* MED-1193 (B9) — themes, plain register, below the Session
                    Summary and above the next-session cards (confirmed 7/13).
                    Server sends null when AI wasn't active → card absent. */}
                <SessionThemesCard
                  themes={summary.themes}
                  aiAvailable={summary.themes != null}
                  viewerRegister="plain"
                />
                {/* MED-1088 (AC1/AC4) — prep nudge shown only when this
                    participant leads the next upcoming session; otherwise the
                    bundle returns null and nothing renders. Sits just above the
                    informational Next Session card, which is unchanged. */}
                {summary.next_session_callout && (
                  <NextSessionCallout
                    sessionId={summary.next_session_callout.session_id}
                    sessionNumber={summary.next_session_callout.session_number}
                    sessionDateIso={summary.next_session_callout.session_date}
                  />
                )}
                <NextSessionSidebarCard next={summary.sidebar_next_session} />
                {/* MED-1199 (Bug 2 / AC7) — the "What your coach sees" card is
                    coach-oriented and references a coach that doesn't exist on a
                    peer-led cohort. Hide it entirely for peer-led groups; the
                    visibility info it carries is already shown inline (the
                    "Shared with Medley" note on Leave Feedback and the "Only you
                    can see this" note on Review Notes), so nothing is lost.
                    Coach-led groups are unchanged (AC8). */}
                {!summary.is_peer_led && (
                  <LeaderVisibilitySidebarCard
                    visibility={summary.sidebar_leader_visibility}
                  />
                )}
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    </>
  );
}
